import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';

const AuthContext = createContext(null);

/** @param {Record<string, unknown> | undefined} claims */
function deriveClaimFlags(claims) {
    return {
        isAdmin: claims?.isAdmin === true,
        isDemo: claims?.isDemo === true,
        isOffline: claims?.isOffline === true,
        mfaEnabled: claims?.mfaEnabled === true,
    };
}

export function AuthProvider({ children }) {
    // Lazy initializer: getAuth(app) returns a singleton in real Firebase, but
    // must still only be looked up once here so `auth` stays referentially
    // stable across re-renders (it is an effect dependency below).
    const [auth] = useState(() => getAuth(getApp()));
    const [user, setUser] = useState(/** @type {import('firebase/auth').User | null} */ (null));
    const [claims, setClaims] = useState(/** @type {Record<string, unknown> | undefined} */ (undefined));
    const [claimFlags, setClaimFlags] = useState(() => deriveClaimFlags(undefined));
    const [isAuthResolved, setIsAuthResolved] = useState(false);
    const [authError, setAuthError] = useState(/** @type {Error | null} */ (null));
    const isMountedRef = useRef(true);
    const requestIdRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const resolveClaims = useCallback(async (currentUser, requestId) => {
        if (!currentUser) {
            if (!isMountedRef.current || requestIdRef.current !== requestId) return;
            setClaims(undefined);
            setClaimFlags(deriveClaimFlags(undefined));
            setAuthError(null);
            return;
        }
        try {
            const tokenResult = await currentUser.getIdTokenResult();
            if (!isMountedRef.current || requestIdRef.current !== requestId) return;
            setClaims(tokenResult.claims);
            setClaimFlags(deriveClaimFlags(tokenResult.claims));
            setAuthError(null);
        } catch (err) {
            if (!isMountedRef.current || requestIdRef.current !== requestId) return;
            setClaims(undefined);
            setClaimFlags(deriveClaimFlags(undefined));
            setAuthError(err instanceof Error ? err : new Error('Failed to read auth claims'));
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
            if (!isMountedRef.current) return;
            const requestId = ++requestIdRef.current;
            setUser(nextUser);
            setIsAuthResolved(false);
            resolveClaims(nextUser, requestId).finally(() => {
                if (isMountedRef.current && requestIdRef.current === requestId) setIsAuthResolved(true);
            });
        });
        return unsubscribe;
    }, [auth, resolveClaims]);

    const refreshClaims = useCallback(
        () => resolveClaims(auth.currentUser, requestIdRef.current),
        [auth, resolveClaims]
    );

    const signOut = useCallback(() => firebaseSignOut(auth), [auth]);

    return (
        <AuthContext.Provider value={{
            auth,
            user,
            claims,
            ...claimFlags,
            isAuthResolved,
            authError,
            signOut,
            refreshClaims,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}

export { AuthContext };
